// apps/dashboard/src/lib/safe-storage.ts
//
// Web Storage THROWS rather than returning null when the browser blocks it:
// mobile Chrome with third-party data off, iOS in-app webviews, Safari private
// mode. Even *reading the property* `window.sessionStorage` can throw, so
// wrapping only the `.getItem()` call leaves the dereference exposed — which is
// why the guard has to sit outside both.
//
// Observed on prod as "SecurityError: Failed to read the 'sessionStorage'
// property from 'Window': Access is denied for this document".
//
// Every accessor degrades instead of throwing: reads return null, writes report
// false. Callers that need durability can branch on the boolean; callers that
// treat storage as a nicety can ignore it. Nothing here should ever be the
// reason a page fails to render.

type StoreName = 'localStorage' | 'sessionStorage';

function get(store: StoreName, key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window[store].getItem(key);
  } catch {
    return null;
  }
}

function set(store: StoreName, key: string, value: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window[store].setItem(key, value);
    return true;
  } catch {
    // Blocked, or quota exceeded.
    return false;
  }
}

function remove(store: StoreName, key: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window[store].removeItem(key);
    return true;
  } catch {
    return false;
  }
}

/** Reads/writes JSON, falling back to `fallback` on blocked storage OR corrupt data. */
function getJSON<T>(store: StoreName, key: string, fallback: T): T {
  const raw = get(store, key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function setJSON(store: StoreName, key: string, value: unknown): boolean {
  try {
    return set(store, key, JSON.stringify(value));
  } catch {
    // Circular structure — JSON.stringify itself threw, not storage.
    return false;
  }
}

export const safeLocal = {
  get: (key: string) => get('localStorage', key),
  set: (key: string, value: string) => set('localStorage', key, value),
  remove: (key: string) => remove('localStorage', key),
  getJSON: <T,>(key: string, fallback: T) => getJSON('localStorage', key, fallback),
  setJSON: (key: string, value: unknown) => setJSON('localStorage', key, value),
};

export const safeSession = {
  get: (key: string) => get('sessionStorage', key),
  set: (key: string, value: string) => set('sessionStorage', key, value),
  remove: (key: string) => remove('sessionStorage', key),
  getJSON: <T,>(key: string, fallback: T) => getJSON('sessionStorage', key, fallback),
  setJSON: (key: string, value: unknown) => setJSON('sessionStorage', key, value),
};
